import React, { useMemo } from "react";
import { useRouter } from "next/router";

import { DEFAULT_LOCALE, Locale, getIntlLocale, toLocale } from "./config";
import { Namespace, getMessages, resolveMessage } from "./messages";

export type TranslationValues = Record<string, string | number>;

/** Maps a tag name in a message to a renderer, e.g. `{ b: (c) => <b>{c}</b> }`. */
export type RichTags = Record<
  string,
  (chunks: React.ReactNode) => React.ReactNode
>;

export interface Translator {
  (key: string, values?: TranslationValues): string;
  /**
   * For messages carrying inline markup: `"Read the <a>guide</a> first"`.
   * Non-nested tags only, which covers the bold/link cases in the marketing
   * copy. Nest something and the inner tag comes through as literal text.
   */
  rich: (
    key: string,
    tags: RichTags,
    values?: TranslationValues,
  ) => React.ReactNode;
  /**
   * For keys that are legitimately absent -- server-supplied taxonomies we
   * translate when we recognise them and pass through otherwise. Returns
   * undefined instead of the key, and stays quiet rather than warning, since a
   * miss here is expected rather than a mistake.
   */
  optional: (key: string, values?: TranslationValues) => string | undefined;
}

/** The active locale, already narrowed. Safe on the server and the client. */
export function useLocale(): Locale {
  return toLocale(useRouter().locale);
}

/**
 * Mirrors next-intl's API on purpose (PRD D3): `const t = useTranslations("about")`
 * then `t("meta.title")`. Keeping the signature identical means an eventual App
 * Router migration swaps this module out without touching call sites.
 */
export function useTranslations(namespace: Namespace): Translator {
  const locale = useLocale();
  return useMemo(
    () => createTranslator(locale, namespace),
    [locale, namespace],
  );
}

/**
 * The non-hook form, for getServerSideProps and other places outside React
 * (the sitemap routes, for instance).
 */
export function createTranslator(
  locale: Locale,
  namespace: Namespace,
): Translator {
  const lookupQuiet = (key: string): string | undefined =>
    resolveMessage(getMessages(locale, namespace), key) ??
    (locale === DEFAULT_LOCALE
      ? undefined
      : resolveMessage(getMessages(DEFAULT_LOCALE, namespace), key));

  const lookup = (key: string): string | undefined => {
    const message = resolveMessage(getMessages(locale, namespace), key);
    if (message !== undefined) return message;

    // A partially translated namespace should render English, not a raw key.
    // Loud in development, silent in production.
    if (locale !== DEFAULT_LOCALE) {
      const fallback = resolveMessage(
        getMessages(DEFAULT_LOCALE, namespace),
        key,
      );
      if (fallback !== undefined) {
        warnOnce(`[i18n] missing ${locale} message "${namespace}.${key}"`);
        return fallback;
      }
    }

    warnOnce(`[i18n] unknown message key "${namespace}.${key}"`);
    return undefined;
  };

  const translator = ((key: string, values?: TranslationValues) => {
    const message = lookup(key);
    // Returning the key keeps a typo visible instead of collapsing the layout.
    if (message === undefined) return key;
    return interpolate(message, locale, values);
  }) as Translator;

  translator.rich = (
    key: string,
    tags: RichTags,
    values?: TranslationValues,
  ) => {
    const message = lookup(key);
    if (message === undefined) return key;
    return renderRich(interpolate(message, locale, values), tags);
  };

  translator.optional = (key: string, values?: TranslationValues) => {
    const message = lookupQuiet(key);
    return message === undefined
      ? undefined
      : interpolate(message, locale, values);
  };

  return translator;
}

/**
 * Resolves ICU plural blocks, then replaces `{name}` placeholders. Unknown
 * placeholders are left as-is.
 */
function interpolate(
  message: string,
  locale: Locale,
  values?: TranslationValues,
): string {
  if (!values) return message;

  return selectPlurals(message, locale, values).replace(
    /\{(\w+)\}/g,
    (match, name) => {
      const value = values[name];
      return value === undefined ? match : String(value);
    },
  );
}

/** `{count, plural, one {...} other {...}}`, with `#` standing for the number. */
const PLURAL_BLOCK = /\{(\w+),\s*plural,\s*((?:[^{}]|\{[^{}]*\})*)\}/g;
const PLURAL_BRANCH = /(=\d+|\w+)\s*\{([^{}]*)\}/g;

/**
 * A deliberately small subset of ICU: named categories plus `=N` exact matches,
 * no nesting and no `offset`. It covers counted nouns, which is the only reason
 * the codebase was concatenating an English "s" onto them.
 *
 * The category comes from `Intl.PluralRules`, so a locale with more forms than
 * English needs new message branches rather than new code. English and Spanish
 * both use one/other, but they disagree about zero: "0 reviews" against
 * "0 reseñas" happens to match, while a language using `zero` would not.
 */
function selectPlurals(
  message: string,
  locale: Locale,
  values: TranslationValues,
): string {
  if (!message.includes(", plural,")) return message;

  return message.replace(PLURAL_BLOCK, (whole, name: string, body: string) => {
    const value = values[name];
    if (typeof value !== "number") return whole;

    const branches = new Map<string, string>();
    PLURAL_BRANCH.lastIndex = 0;
    let branch: RegExpExecArray | null;
    while ((branch = PLURAL_BRANCH.exec(body)) !== null) {
      branches.set(branch[1], branch[2]);
    }

    const category = new Intl.PluralRules(getIntlLocale(locale)).select(value);
    const chosen =
      branches.get(`=${value}`) ??
      branches.get(category) ??
      branches.get("other");

    if (chosen === undefined) {
      warnOnce(`[i18n] no "${category}" or "other" branch in "${message}"`);
      return whole;
    }

    return chosen.replace(/#/g, String(value));
  });
}

const RICH_TAG = /<(\w+)>([\s\S]*?)<\/\1>/g;

function renderRich(message: string, tags: RichTags): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  RICH_TAG.lastIndex = 0;

  while ((match = RICH_TAG.exec(message)) !== null) {
    const [whole, tagName, inner] = match;
    const render = tags[tagName];

    if (!render) {
      warnOnce(`[i18n] no renderer for <${tagName}> in "${message}"`);
      continue;
    }

    if (match.index > cursor) {
      parts.push(message.slice(cursor, match.index));
    }
    parts.push(
      React.createElement(
        React.Fragment,
        { key: `${tagName}-${match.index}` },
        render(inner),
      ),
    );
    cursor = match.index + whole.length;
  }

  if (cursor < message.length) parts.push(message.slice(cursor));

  return parts.length === 1 ? parts[0] : parts;
}

const warned = new Set<string>();

function warnOnce(message: string): void {
  if (process.env.NODE_ENV === "production") return;
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(message);
}
